import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COMMENTS } from "@/constants/testIds";
import { Trash2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Groups a flat list of comments into top-level items with replies threaded underneath.
 */
function threadComments(comments) {
  const byId = new Map();
  const tops = [];
  comments.forEach(c => byId.set(c.comment_id, { ...c, replies: [] }));
  byId.forEach(c => {
    if (c.parent_comment_id && byId.has(c.parent_comment_id)) {
      byId.get(c.parent_comment_id).replies.push(c);
    } else {
      tops.push(c);
    }
  });
  return tops;
}

export default function CommentsSection({ blogId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [moderation, setModeration] = useState("auto");

  const load = () => api.get(`/blogs/${blogId}/comments`).then(r => setComments(r.data));
  useEffect(() => {
    if (blogId) load();
    api.get("/site-settings/comments").then(r => setModeration(r.data.moderation)).catch(() => {});
  /* eslint-disable-next-line */ }, [blogId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast("Sign in to comment");
    if (!body.trim()) return;
    await api.post(`/blogs/${blogId}/comments`, { body });
    setBody("");
    load();
    if (moderation === "review" && user.role !== "admin") {
      toast("Thanks — your comment is awaiting review.");
    }
  };
  const del = async (id) => {
    if (!confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await api.delete(`/comments/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete — please try again.");
    }
  };
  const submitReply = async (parentId) => {
    const t = replyBody.trim();
    if (!t) return;
    try {
      await api.post(`/blogs/${blogId}/comments`, { body: t, parent_comment_id: parentId });
      setReplyBody(""); setReplyingTo(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post reply.");
    }
  };

  const threaded = threadComments(comments);

  return (
    <div className="mt-16 pt-10 border-t border-slate-200">
      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
        Discussion <span className="text-slate-400">· {comments.length}</span>
      </h3>

      {user ? (
        <form data-testid={COMMENTS.form} onSubmit={submit} className="flex gap-2 mb-6">
          <Input
            data-testid={COMMENTS.input}
            value={body} onChange={e => setBody(e.target.value)}
            placeholder="Share a thought…" className="border-slate-300"
          />
          <Button data-testid={COMMENTS.submit} type="submit" className="bg-sky-700 hover:bg-sky-600">Post</Button>
        </form>
      ) : (
        <div className="mb-6 text-sm text-slate-500 border border-dashed border-slate-300 rounded p-4">
          Sign in to join the discussion.
        </div>
      )}

      <ol className="space-y-6">
        {threaded.map(c => (
          <li key={c.comment_id} data-testid={COMMENTS.item(c.comment_id)} className="flex gap-3 items-start">
            {c.user_picture
              ? <img src={c.user_picture} alt="" className="w-8 h-8 rounded-full"/>
              : <div className="w-8 h-8 rounded-full bg-slate-200"/>}
            <div className="flex-1">
              <div className="text-sm flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">{c.user_name}</span>
                {c.approved === false && (
                  <span data-testid={`comment-pending-${c.comment_id}`}
                        className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5"
                        title="Awaiting admin approval — only you can see this until it's approved">
                    Pending
                  </span>
                )}
                <span className="text-slate-400 ml-2 text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{c.body}</div>
              <div className="mt-1.5 flex items-center gap-3 text-xs uppercase tracking-widest">
                {user && (
                  <button data-testid={`comment-reply-${c.comment_id}`}
                          onClick={() => { setReplyingTo(replyingTo === c.comment_id ? null : c.comment_id); setReplyBody(""); }}
                          className="text-slate-500 hover:text-sky-700 inline-flex items-center gap-1">
                    <CornerDownRight className="w-3.5 h-3.5"/>{replyingTo === c.comment_id ? "Cancel" : "Reply"}
                  </button>
                )}
                {(user && (user.user_id === c.user_id || user.role === "admin")) && (
                  <button data-testid={COMMENTS.delete(c.comment_id)} onClick={() => del(c.comment_id)} className="text-slate-400 hover:text-red-600 inline-flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5"/>Delete
                  </button>
                )}
              </div>

              {replyingTo === c.comment_id && (
                <form onSubmit={(e) => { e.preventDefault(); submitReply(c.comment_id); }}
                      className="mt-2 border-l-2 border-sky-600 pl-3 flex gap-2">
                  <Input data-testid={`comment-reply-input-${c.comment_id}`} value={replyBody}
                         onChange={e => setReplyBody(e.target.value)} placeholder="Write a reply…"
                         className="border-slate-300"/>
                  <Button data-testid={`comment-reply-submit-${c.comment_id}`} type="submit" className="bg-sky-700 hover:bg-sky-600">Reply</Button>
                </form>
              )}

              {c.replies.length > 0 && (
                <ul className="mt-3 space-y-3 border-l-2 border-slate-200 pl-4">
                  {c.replies.map(rp => (
                    <li key={rp.comment_id} className="flex gap-3 items-start">
                      {rp.user_picture
                        ? <img src={rp.user_picture} alt="" className="w-7 h-7 rounded-full"/>
                        : <div className="w-7 h-7 rounded-full bg-slate-200"/>}
                      <div className="flex-1">
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{rp.user_name}</span>
                          {rp.is_admin_reply && (
                            <span className="text-[10px] uppercase tracking-widest bg-sky-700 text-white rounded px-1.5 py-0.5">Admin</span>
                          )}
                          <span className="text-slate-400 text-xs">{new Date(rp.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{rp.body}</div>
                        {(user && (user.user_id === rp.user_id || user.role === "admin")) && (
                          <button onClick={() => del(rp.comment_id)}
                                  className="mt-1 text-slate-400 hover:text-red-600 text-xs uppercase tracking-widest inline-flex items-center gap-1">
                            <Trash2 className="w-3 h-3"/>Delete
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
